import React, { useCallback, useEffect, useState } from "react";
import { Building2, Eye, FileText, BadgeCheck, DollarSign, AlertCircle } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import Badge from "../common/Badge";
import { formatMoneyDisplay } from "../../utils/money";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import LeadChat from "../common/LeadChat";
import { SkeletonStack } from "../common/Skeleton";
import { STRIPE_ENABLED } from "../../featureFlags";
import { buildInvoiceResponseRpcArgs } from "../../utils/publicTokenPayload";

// Customer-facing invoice view, opened via ?invoice=<public_token>.
// Mirrors CustomerQuoteView for consistency. Shows the line items,
// amendments (vs the original quote), and total. Customer can
// acknowledge the invoice and (when STRIPE_ENABLED) pay online; until
// then payment is tracked offline (cash / bank transfer) and the
// customer marks paid manually OR the tradie does it from their end.
export default function CustomerInvoiceView({ token, onBackToApp, refreshMainApp, session }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [invoiceRow, setInvoiceRow] = useState(null);
  const [leadRow, setLeadRow] = useState(null);
  const [businessRow, setBusinessRow] = useState(null);
  const [error, setError] = useState("");
  const [responding, setResponding] = useState(false);

  const loadInvoice = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError("");

      const { data: payload, error: invoiceError } = await supabase
        .rpc("rqp_get_public_invoice", { p_token: token });

      if (invoiceError) throw invoiceError;
      const invoice = payload?.invoice || null;
      if (!invoice) {
        setError("Invoice not found.");
        setLoading(false);
        return;
      }

      setInvoiceRow(invoice);
      setLeadRow(payload?.lead || null);
      setBusinessRow(payload?.business || null);
    } catch (err) {
      setError(err.message || "Failed to load invoice.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  async function handleAcknowledge() {
    if (!invoiceRow) return;
    try {
      setResponding(true);
      const now = new Date().toISOString();
      const { data: payload, error: updateError } = await supabase
        .rpc(
          "rqp_submit_invoice_response",
          buildInvoiceResponseRpcArgs({
            token,
            action: "acknowledged",
          })
        );

      if (updateError) throw updateError;

      setInvoiceRow((prev) =>
        prev ? { ...prev, ...(payload?.invoice || {}), customer_status: "acknowledged", acknowledged_at: now, customer_responded_at: now } : prev
      );
      await refreshMainApp?.();
      showToast("Invoice acknowledged. Thank you.", "success");
    } catch (err) {
      showToast(err.message || "Could not acknowledge invoice.", "error");
    } finally {
      setResponding(false);
    }
  }

  async function handleMarkPaid(method) {
    if (!invoiceRow) return;
    try {
      setResponding(true);
      const now = new Date().toISOString();
      const { data: payload, error: updateError } = await supabase
        .rpc(
          "rqp_submit_invoice_response",
          buildInvoiceResponseRpcArgs({
            token,
            action: "paid",
            paidMethod: method,
          })
        );

      if (updateError) throw updateError;

      setInvoiceRow((prev) =>
        prev
          ? {
              ...prev,
              ...(payload?.invoice || {}),
              customer_status: "paid",
              paid_amount: invoiceRow.total,
              paid_at: now,
              paid_method: method,
              customer_responded_at: now,
            }
          : prev
      );
      await refreshMainApp?.();
      showToast("Marked as paid. The business will confirm receipt.", "success");
    } catch (err) {
      showToast(err.message || "Could not mark paid.", "error");
    } finally {
      setResponding(false);
    }
  }

  if (loading) {
    return (
      <div className="page customer-shell">
        <Card>
          <SkeletonStack lines={5} widths={["55%", "100%", "90%", "85%", "40%"]} />
        </Card>
      </div>
    );
  }

  if (error || !invoiceRow) {
    return (
      <div className="page customer-shell">
        <Card>
          <h2>Invoice unavailable</h2>
          <p className="muted">{error || "Invoice not found."}</p>
          <div className="button-row">
            <Button secondary onClick={onBackToApp}>
              Back to app
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const lineItems = Array.isArray(invoiceRow.line_items) ? invoiceRow.line_items : [];
  const isPaid = invoiceRow.customer_status === "paid";
  const isAcknowledged = invoiceRow.customer_status === "acknowledged" || isPaid;

  return (
    <div className="page customer-shell">
      <div className="two-grid">
        <Card className="hero dark">
          <Badge>Invoice</Badge>
          <h1>{invoiceRow.invoice_number || "Invoice"}</h1>
          <p>
            Please review the invoice details below. The business has marked the job complete and is now requesting payment.
          </p>

          <div className="button-row">
            <Badge>Status: {invoiceRow.customer_status || "pending"}</Badge>
            {invoiceRow.viewed_at && (
              <Badge>
                <Eye size={14} /> Viewed
              </Badge>
            )}
          </div>
        </Card>

        <Card>
          <h2>
            <Building2 size={18} /> {businessRow?.name || "Business invoice"}
          </h2>

          <p className="muted">
            {leadRow?.trade || "Trade"} - {leadRow?.project_type || "Project"}
            {leadRow?.suburb ? ` ~ ${leadRow.suburb}` : ""}
          </p>

          <div className="estimate-box">
            {invoiceRow.gst_registered !== false ? (
              <>
                <div className="muted small">Subtotal</div>
                <div className="estimate-range">{formatMoneyDisplay(invoiceRow.subtotal || 0)}</div>

                <div className="muted small">GST</div>
                <div className="estimate-range">{formatMoneyDisplay(invoiceRow.gst || 0)}</div>

                <div className="muted small">Total (inc GST)</div>
                <div className="estimate-range">
                  <strong>{formatMoneyDisplay(invoiceRow.total || 0)}</strong>
                </div>
              </>
            ) : (
              <>
                <div className="muted small">Total</div>
                <div className="estimate-range">
                  <strong>{formatMoneyDisplay(invoiceRow.total || 0)}</strong>
                </div>
                <p className="muted small" style={{ margin: "8px 0 0" }}>
                  GST not applicable — supplier not GST registered.
                </p>
              </>
            )}
          </div>
        </Card>
      </div>

      {invoiceRow.amendments_summary && (
        <Card>
          <h3>
            <AlertCircle size={18} /> Amendments since the original quote
          </h3>
          <p className="muted" style={{ whiteSpace: "pre-wrap" }}>
            {invoiceRow.amendments_summary}
          </p>
        </Card>
      )}

      <div className="two-grid">
        <Card>
          <h3>
            <FileText size={18} /> Invoice breakdown
          </h3>

          {lineItems.length > 0 ? (
            <div className="lead-list">
              {lineItems.map((item, index) => (
                <div key={index} className="lead-card">
                  <div className="lead-top">
                    <div className="lead-name">{item.description || "Line item"}</div>
                    <div className="feature-title">{formatMoneyDisplay(item.amount || 0)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">No line items were provided.</p>
          )}

          {invoiceRow.intro_text && (
            <div className="estimate-box">
              <div className="muted small">Notes</div>
              <div className="muted">{invoiceRow.intro_text}</div>
            </div>
          )}
        </Card>

        <Card>
          <h3>
            <DollarSign size={18} /> Payment
          </h3>

          {isPaid && (
            <div className="estimate-box">
              <div className="estimate-range">
                <strong>Paid</strong>
              </div>
              <p className="muted">
                Thank you. Marked as paid via{" "}
                <strong>{invoiceRow.paid_method || "offline"}</strong> on{" "}
                {invoiceRow.paid_at ? new Date(invoiceRow.paid_at).toLocaleDateString() : "—"}.
              </p>
            </div>
          )}

          {!isPaid && (
            <>
              {!isAcknowledged && (
                <p className="muted">
                  Please acknowledge that you've received this invoice. You can pay using any method shown below.
                </p>
              )}

              <div className="button-row" style={{ flexWrap: "wrap", gap: "10px" }}>
                {!isAcknowledged && (
                  <Button onClick={handleAcknowledge} disabled={responding}>
                    <BadgeCheck size={16} /> Acknowledge invoice
                  </Button>
                )}

                {/* Stripe pay-now CTA gated behind STRIPE_ENABLED. While off,
                    only offline mark-paid options are visible. */}
                {STRIPE_ENABLED && (
                  <Button onClick={() => showToast("Stripe checkout not yet wired up.", "info")} disabled={responding}>
                    <DollarSign size={16} /> Pay now
                  </Button>
                )}

                <Button secondary onClick={() => handleMarkPaid("bank_transfer")} disabled={responding}>
                  Mark paid (bank transfer)
                </Button>
                <Button secondary onClick={() => handleMarkPaid("cash")} disabled={responding}>
                  Mark paid (cash)
                </Button>
              </div>

              <p className="muted small" style={{ marginTop: "12px" }}>
                For bank transfer details or any questions, please message the business below.
              </p>
            </>
          )}

          <div className="button-row" style={{ marginTop: "14px" }}>
            <Button secondary onClick={onBackToApp}>
              Back to app
            </Button>
          </div>
        </Card>
      </div>

      <Card>
        <h3 style={{ margin: "0 0 10px", fontSize: "1rem" }}>Messages</h3>
        <LeadChat
          leadId={invoiceRow.lead_id}
          senderRole="customer"
          senderName={(leadRow?.customer_name || "Customer").split(" ")[0]}
          businessId={invoiceRow.business_id || null}
        />
      </Card>
    </div>
  );
}
